import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, UserRound, CircleDollarSign, Eye, Sparkles, Gauge } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import BioWriterDialog from "@/components/BioWriterDialog";

const ALL_SUBJECTS = ["French", "English", "Spanish", "Math"];
const LEVELS = ["Beginner", "Intermediate", "Advanced", "AP/College"];
const AVAIL = ["Weekdays Mornings", "Weekdays Evenings", "Weekends", "Saturdays", "Sundays"];

const statusColor = {
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  accepted: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  declined: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
};

export default function Dashboard() {
  const { user, refreshMe } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(true);

  const [form, setForm] = useState({
    name: user?.name || "",
    headline: user?.headline || "",
    bio: user?.bio || "",
    photo: user?.photo || "",
    hourly_rate: user?.hourly_rate || 40,
    subjects: user?.subjects || [],
    languages: (user?.languages || []).join(", "),
    level: user?.level || "Intermediate",
    availability: user?.availability || [],
    published: user?.published ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const [bioScore, setBioScore] = useState(null);
  const [scoring, setScoring] = useState(false);

  const checkBioScore = async () => {
    setScoring(true);
    try {
      const { data } = await api.get("/tutors/me/bio-score");
      setBioScore(data);
    } catch (err) {
      toast.error("Could not score bio right now");
    } finally {
      setScoring(false);
    }
  };

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        headline: user.headline || "",
        bio: user.bio || "",
        photo: user.photo || "",
        hourly_rate: user.hourly_rate || 40,
        subjects: user.subjects || [],
        languages: (user.languages || []).join(", "),
        level: user.level || "Intermediate",
        availability: user.availability || [],
        published: user.published ?? false,
      });
    }
  }, [user]);

  const loadRequests = async () => {
    setLoadingReqs(true);
    try {
      const { data } = await api.get("/tutors/me/requests");
      setRequests(data.requests);
    } finally {
      setLoadingReqs(false);
    }
  };

  useEffect(() => { loadRequests(); }, []);

  const toggleSubject = (s) =>
    setForm((f) => ({
      ...f,
      subjects: f.subjects.includes(s) ? f.subjects.filter((x) => x !== s) : [...f.subjects, s],
    }));

  const toggleAvail = (a) =>
    setForm((f) => ({
      ...f,
      availability: f.availability.includes(a) ? f.availability.filter((x) => x !== a) : [...f.availability, a],
    }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/tutors/me", {
        name: form.name,
        headline: form.headline,
        bio: form.bio,
        photo: form.photo,
        hourly_rate: Number(form.hourly_rate),
        subjects: form.subjects,
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        level: form.level,
        availability: form.availability,
        published: form.published,
      });
      await refreshMe();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/tutors/me/requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Request ${status}`);
    } catch (err) {
      toast.error("Could not update request");
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    accepted: requests.filter((r) => r.status === "accepted").length,
    revenue: requests.filter((r) => r.status === "accepted").length * (user?.hourly_rate || 0),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1">
          {form.published ? (
            <>Your profile is <span className="text-emerald-600 font-medium">live</span> on the marketplace.</>
          ) : (
            <>Your profile is <span className="text-amber-600 font-medium">hidden</span>. Complete it and toggle it live.</>
          )}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={<Inbox className="h-5 w-5" />} label="Total requests" value={stats.total} testid="stat-total" />
        <StatCard icon={<UserRound className="h-5 w-5" />} label="Pending" value={stats.pending} testid="stat-pending" />
        <StatCard icon={<Eye className="h-5 w-5" />} label="Accepted" value={stats.accepted} testid="stat-accepted" />
        <StatCard icon={<CircleDollarSign className="h-5 w-5" />} label="Projected" value={`$${stats.revenue}`} testid="stat-revenue" />
      </div>

      <Tabs defaultValue="requests">
        <TabsList className="rounded-full">
          <TabsTrigger value="requests" className="rounded-full" data-testid="tutor-dashboard-requests-tab">
            Session requests
          </TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full" data-testid="tutor-dashboard-profile-tab">
            My profile
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-6">
          {loadingReqs ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : requests.length === 0 ? (
            <Card className="p-12 text-center">
              <Inbox className="h-10 w-10 mx-auto text-muted-foreground" />
              <h3 className="mt-4 font-heading text-xl font-semibold">No session requests yet</h3>
              <p className="text-muted-foreground mt-1 max-w-md mx-auto">
                Share your profile link once it's published — students will reach out here.
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <Card key={r.id} className="p-5" data-testid="request-row">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold">{r.student_name}</h4>
                        <Badge className={`rounded-full font-medium capitalize ${statusColor[r.status] || ""}`}>
                          {r.status}
                        </Badge>
                        {r.paid && !r.refunded && (
                          <Badge className="rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                            Paid
                          </Badge>
                        )}
                        {r.refunded && (
                          <Badge className="rounded-full font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            Refunded
                          </Badge>
                        )}
                        {r.ai_category && (
                          <Badge variant="outline" className="rounded-full font-mono text-[10px]">
                            {r.ai_category}
                          </Badge>
                        )}
                        {r.ai_difficulty && (
                          <Badge variant="outline" className="rounded-full font-mono text-[10px]">
                            {r.ai_difficulty}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{r.student_email}</p>
                      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                        <span><span className="text-muted-foreground">Subject:</span> {r.subject}</span>
                        <span><span className="text-muted-foreground">Level:</span> {r.level || "—"}</span>
                        <span><span className="text-muted-foreground">Date:</span> {r.preferred_date}</span>
                        <span><span className="text-muted-foreground">Time:</span> {r.preferred_time || "—"}</span>
                        {r.amount_cents != null && (
                          <span><span className="text-muted-foreground">Fee:</span> ${(r.amount_cents / 100).toFixed(2)}</span>
                        )}
                      </div>
                      {r.message && (
                        <p className="mt-3 text-sm text-muted-foreground border-l-2 border-primary/40 pl-3">
                          {r.message}
                        </p>
                      )}
                    </div>
                    {r.status === "pending" && (
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => updateStatus(r.id, "accepted")}
                          className="rounded-full"
                          data-testid="tutor-request-accept-button"
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(r.id, "declined")}
                          className="rounded-full"
                          data-testid="tutor-request-decline-button"
                        >
                          {r.paid ? "Decline & refund" : "Decline"}
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <Card className="p-6 lg:p-8">
            <form onSubmit={saveProfile} className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4 pb-4 border-b border-border">
                <div>
                  <h3 className="font-heading text-xl font-semibold">Public profile</h3>
                  <p className="text-sm text-muted-foreground">Toggle to show/hide your card in the marketplace.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="pub-switch" className="font-medium">
                    {form.published ? "Live" : "Hidden"}
                  </Label>
                  <Switch
                    id="pub-switch"
                    checked={form.published}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))}
                    data-testid="profile-published-switch"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="profile-name-input"
                  />
                </Field>
                <Field label="Headline (shown on your card)">
                  <Input
                    value={form.headline}
                    onChange={(e) => setForm({ ...form, headline: e.target.value })}
                    placeholder="Native French tutor · 6 yrs experience"
                    data-testid="profile-headline-input"
                  />
                </Field>
                <Field label="Hourly rate ($)">
                  <Input
                    type="number"
                    min={10}
                    max={300}
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                    data-testid="profile-rate-input"
                  />
                </Field>
                <Field label="Level">
                  <select
                    value={form.level}
                    onChange={(e) => setForm({ ...form, level: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                    data-testid="profile-level-select"
                  >
                    {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Profile photo URL">
                  <Input
                    value={form.photo}
                    onChange={(e) => setForm({ ...form, photo: e.target.value })}
                    placeholder="https://…"
                    data-testid="profile-photo-input"
                  />
                </Field>
                <Field label="Languages (comma-separated)">
                  <Input
                    value={form.languages}
                    onChange={(e) => setForm({ ...form, languages: e.target.value })}
                    placeholder="English, French"
                    data-testid="profile-languages-input"
                  />
                </Field>
              </div>

              <Field label="Short bio">
                <div className="flex justify-end gap-3 -mb-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={checkBioScore}
                    disabled={scoring || !form.bio}
                    className="h-7 text-xs"
                    data-testid="bio-score-button"
                  >
                    <Gauge className="h-3.5 w-3.5 mr-1" />
                    {scoring ? "Scoring…" : "Score my bio"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBioOpen(true)}
                    className="h-7 text-xs text-primary hover:text-primary"
                    data-testid="open-bio-writer-button"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Draft with AI
                  </Button>
                </div>
                <Textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  placeholder="Tell students what makes your lessons effective…"
                  data-testid="profile-bio-input"
                />
                {bioScore && (
                  <div className="mt-2 rounded-lg border border-border bg-muted/40 p-3" data-testid="bio-score-result">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center justify-center h-10 w-14 rounded-md font-heading font-bold text-lg ${
                        bioScore.score_0_100 >= 75
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                          : bioScore.score_0_100 >= 50
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300"
                          : "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300"
                      }`}>{bioScore.score_0_100}</span>
                      <div className="flex-1 text-sm">
                        <p className="font-medium">Bio quality score</p>
                        <p className="text-muted-foreground text-xs mt-0.5">{bioScore.feedback}</p>
                      </div>
                    </div>
                  </div>
                )}
              </Field>

              <Field label="Subjects you teach">
                <div className="flex flex-wrap gap-4">
                  {ALL_SUBJECTS.map((s) => (
                    <label key={s} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={form.subjects.includes(s)}
                        onCheckedChange={() => toggleSubject(s)}
                        data-testid={`profile-subject-${s.toLowerCase()}`}
                      />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Availability">
                <div className="flex flex-wrap gap-2">
                  {AVAIL.map((a) => (
                    <Button
                      type="button"
                      key={a}
                      variant={form.availability.includes(a) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleAvail(a)}
                      className="rounded-full"
                      data-testid={`profile-avail-${a.replace(/\s/g, "-").toLowerCase()}`}
                    >
                      {a}
                    </Button>
                  ))}
                </div>
              </Field>

              <div className="flex justify-end gap-3">
                <Button
                  type="submit"
                  disabled={saving}
                  className="rounded-full"
                  data-testid="profile-save-button"
                >
                  {saving ? "Saving…" : "Save profile"}
                </Button>
              </div>
            </form>
          </Card>
        </TabsContent>
      </Tabs>

      <BioWriterDialog
        open={bioOpen}
        onOpenChange={setBioOpen}
        name={form.name}
        subjects={form.subjects}
        onApply={(draft) => setForm((f) => ({ ...f, headline: draft.headline, bio: draft.bio }))}
      />
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</p>
          <p className="font-heading text-2xl font-bold">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
